/* ============================================================
   src/shared/estimateFile.ts
   ============================================================
   File-write helpers for the estimate snapshot. Used by both
   the desktop/web App and the mobile shell so every platform
   produces a byte-identical .DUest file with the same default
   filename rules.

   Pure refactor of helpers that used to live inline in App.tsx
   (EST_EXT, defaultFileName, normalizeFileName, downloadTextFile).

   shareEstimateFile() is the mobile-friendly addition: it
   prefers navigator.share with a File attachment (iOS share
   sheet → Save to Files, Dropbox, Mail, …) and falls back to
   the same anchor-download path the web app already uses.
   ============================================================ */

export const EST_EXT = ".est";

/** Legacy extension from the Decks-Unique-branded builds. Still accepted on
 *  open everywhere; on save we rewrite it to EST_EXT so no new .DUest files
 *  are produced. Kept as a constant so the open filters and normalize logic
 *  stay in sync. */
export const LEGACY_EST_EXT = ".DUest";

export type DefaultFileNameInput = {
  town?: string;
  lastName?: string;
  estimateName?: string;
};

/** Build the default Save filename. Prefers an explicit estimateName,
 *  else "{Town}_{LastName}", else "Estimate". Always returns a name
 *  ending in EST_EXT (.est) with internal whitespace collapsed to underscores. */
export function defaultFileName(input: DefaultFileNameInput): string {
  const town = (input.town || "").trim();
  const last = (input.lastName || "").trim();
  const base = [town, last].filter(Boolean).join("_").trim() || "Estimate";
  const name = (input.estimateName || "").trim();
  const file = `${name || base}${EST_EXT}`;
  return file.replace(/\s+/g, "_");
}

/** Coerce a user-typed filename to EST_EXT (.est), trimming whitespace.
 *  Legacy trailing extensions (.json, and the old .DUest) are rewritten to
 *  EST_EXT rather than appended, so a user typing "Foo.DUest" gets "Foo.est"
 *  — never "Foo.DUest.est". Returns "" for empty input. */
export function normalizeFileName(name: string): string {
  const trimmed = (name || "").trim();
  if (!trimmed) return "";
  const lower = trimmed.toLowerCase();
  if (lower.endsWith(EST_EXT.toLowerCase())) return trimmed;
  if (lower.endsWith(".json"))
    return trimmed.slice(0, -".json".length) + EST_EXT;
  if (lower.endsWith(LEGACY_EST_EXT.toLowerCase()))
    return trimmed.slice(0, -LEGACY_EST_EXT.length) + EST_EXT;
  return trimmed + EST_EXT;
}

/** Base64-encode UTF-8 text for an email attachment (Resend wants base64
 *  with no "data:" prefix). btoa() alone mangles non-ASCII, so we round-trip
 *  through encodeURIComponent first — the standard browser-safe pattern. */
export function textToBase64(text: string): string {
  return btoa(unescape(encodeURIComponent(text || "")));
}

/** Trigger a browser download via a hidden anchor + object URL. Web's
 *  default save path; also the fallback when Web Share isn't available. */
export function downloadTextFile(text: string, filename: string): void {
  const blob = new Blob([text], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** True when a filename looks like one of ours (.est, legacy .DUest, or the
 *  .json some early saves produced). Used to warn — never to block — because
 *  iOS renames duplicates ("Smith 2.est") and cloud providers occasionally
 *  hand back a name with no extension at all. Content validation in
 *  parseEstimateFileText is what actually decides. */
export function isEstimateFileName(name: string): boolean {
  const lower = (name || "").trim().toLowerCase();
  return (
    lower.endsWith(EST_EXT) ||
    lower.endsWith(LEGACY_EST_EXT.toLowerCase()) ||
    lower.endsWith(".json")
  );
}

export type ParsedEstimateFile =
  | { ok: true; snapshot: any }
  | { ok: false; error: string };

/** Parse the text of a picked file into an estimate snapshot.
 *
 *  This is the mobile Open path's gatekeeper. Mobile can't filter the iOS
 *  Files picker reliably (see the accept= note at the call site), so the user
 *  CAN hand us a photo, a PDF, or a Word doc — this has to reject that with a
 *  sentence a human understands rather than throwing a raw SyntaxError.
 *
 *  Accepts any JSON object that carries at least one field we recognize from
 *  buildSnapshot(). Deliberately loose: snapshots have changed shape across
 *  versions and an old .DUest must still open. */
export function parseEstimateFileText(text: string): ParsedEstimateFile {
  const raw = (text || "").trim();
  if (!raw) {
    return { ok: false, error: "That file is empty." };
  }

  let parsed: any;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {
      ok: false,
      error:
        "That doesn't look like an estimate file. Pick a .est file saved by the Estimator.",
    };
  }

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return {
      ok: false,
      error:
        "That doesn't look like an estimate file. Pick a .est file saved by the Estimator.",
    };
  }

  // A snapshot is recognized by carrying at least one known key. Checking a
  // set rather than requiring estimateId specifically, because estimateId is
  // only stamped on first edit — a saved-but-barely-touched file may lack it.
  const KNOWN_KEYS = [
    "estimateId",
    "estimateName",
    "clientLastName",
    "clientFirstName",
    "clientTown",
    "selectedDeckingId",
    "deckingSqFt",
    "addItems",
    "pricingItems",
    "sowText",
    "contract",
  ];
  const looksLikeSnapshot = KNOWN_KEYS.some((k) =>
    Object.prototype.hasOwnProperty.call(parsed, k)
  );
  if (!looksLikeSnapshot) {
    return {
      ok: false,
      error:
        "That file is valid JSON but isn't an estimate. Pick a .est file saved by the Estimator.",
    };
  }

  return { ok: true, snapshot: parsed };
}

export type ShareResult =
  | { ok: true; via: "share" | "download" }
  | { ok: false; via: "share" | "download"; canceled?: boolean; error?: string };

/** Save an estimate file from the user's current context.
 *
 *  Prefers navigator.share with a File attachment (native iOS share
 *  sheet → Save to Files / Dropbox / Mail / AirDrop). Falls back to
 *  anchor download if Web Share with files isn't supported.
 *
 *  Must be invoked inside a user gesture (button tap). User-cancel of
 *  the share sheet is treated as success-no-op (no error noise).
 */
export async function shareEstimateFile(
  text: string,
  filename: string
): Promise<ShareResult> {
  const nav: any = typeof navigator !== "undefined" ? navigator : null;
  const canUseShare =
    !!nav &&
    typeof nav.share === "function" &&
    typeof File === "function";

  if (canUseShare) {
    try {
      const file = new File([text], filename, { type: "application/json" });
      const payload: any = { files: [file], title: filename };
      // canShare is the spec-mandated capability probe for file shares.
      if (typeof nav.canShare === "function" && !nav.canShare(payload)) {
        // Fall through to download.
      } else {
        await nav.share(payload);
        return { ok: true, via: "share" };
      }
    } catch (err: any) {
      // AbortError = user dismissed the share sheet. Not an error.
      const name = err && (err.name || err.code);
      if (name === "AbortError" || name === 20) {
        return { ok: false, via: "share", canceled: true };
      }
      // Real failure — fall back to download so the user still gets a file.
      try {
        downloadTextFile(text, filename);
        return { ok: true, via: "download" };
      } catch (err2: any) {
        return {
          ok: false,
          via: "download",
          error: err2?.message || String(err2),
        };
      }
    }
  }

  try {
    downloadTextFile(text, filename);
    return { ok: true, via: "download" };
  } catch (err: any) {
    return { ok: false, via: "download", error: err?.message || String(err) };
  }
}
